/**
 * @fileoverview Clock arithmetic for time-of-day on entries (MOR-935)
 * @module timesheets/clock
 *
 * Purpose:
 * - Add minutes to an HH:MM clock time (wrapping past midnight)
 * - Work out where the day's last timed entry ends, so a new entry can roll
 *   on from it rather than needing the time typed
 *
 * WLPR's people who log against the clock do a five-minute task across five
 * jobs and want the sequence. Entries with a start time carry their end
 * either explicitly (a stopwatch window) or as start plus their units.
 *
 * Pure by design (no ctx, no db) so the client and the server share it.
 *
 * Used by:
 * - convex/timesheets/entries.ts (dayWindow)
 * - src/components/timesheets/* (the Log time dialog and compose row)
 */

/** The start of a day's first timed entry when nothing says otherwise. */
export const DEFAULT_DAY_START = "09:00";

/** HH:MM plus a number of minutes, wrapping at midnight. "23:50" + 20 = "00:10". */
export function addMinutesToClock(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = (((h * 60 + m + Math.round(minutes)) % 1440) + 1440) % 1440;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export interface TimedEntry {
  startTime?: string;
  endTime?: string;
  units: number;
}

/** When an entry ends: its recorded end, else its start plus its units. */
export function entryEndTime(
  entry: TimedEntry,
  unitMinutes: number,
): string | null {
  if (!entry.startTime) return null;
  return (
    entry.endTime ??
    addMinutesToClock(entry.startTime, entry.units * unitMinutes)
  );
}

/**
 * Where a new entry on this day should start: after the latest end among the
 * day's timed entries, or the default day start when none has a time yet.
 * Untimed entries ("posted to the day") are ignored — they say nothing about
 * the clock.
 */
export function rollOnStartTime(
  entries: readonly TimedEntry[],
  unitMinutes: number,
): string {
  const ends = entries.flatMap((e) => {
    const end = entryEndTime(e, unitMinutes);
    return end ? [end] : [];
  });
  return (
    ends.reduce((latest, end) => (end > latest ? end : latest), "") ||
    DEFAULT_DAY_START
  );
}
